import type { ReactNode } from 'react';
import {
  CC_STATUS_LABELS,
  ccDelayBand,
  ccFormatElapsed,
  type CallCenterOrder,
} from '@matix/types';
import { useLang } from '../../lib/language';
import { useDriverName } from './driverName';
import { elapsedSeconds, useMoneyFormatter } from './hooks';
import { formatReceiptDateTime } from '../receipts/helpers';

/**
 * One order, as a card.
 *
 * Shared by the agent dashboard and the branch pending list rather than
 * written twice. The two screens differ in what you can DO with an order, not
 * in what an order IS — so the actions come in as children and everything
 * above them is identical wherever it is rendered. That matters more than the
 * saved lines: an order that reads "Ready · 14:32 · 87.50 SAR" on the phone and
 * something subtly different at the counter is how two people on a call end up
 * describing different orders.
 *
 * The delay chip is the point of the card. It is coloured by the SLA band, it
 * counts up once a second, and it stops when the order stops.
 */
export function OrderCard({
  order,
  nowMs,
  warnSeconds,
  criticalSeconds,
  onOpen,
  children,
  showBranch = false,
}: {
  order: CallCenterOrder;
  nowMs: number;
  warnSeconds: number;
  criticalSeconds: number;
  onOpen?: () => void;
  children?: ReactNode;
  showBranch?: boolean;
}): JSX.Element {
  const { t, lang } = useLang();
  const money = useMoneyFormatter();
  const driverName = useDriverName();

  const driverLabel = driverName(order);
  const elapsed = elapsedSeconds(order, nowMs);
  const band = ccDelayBand(order.status, elapsed, warnSeconds, criticalSeconds);
  const statusLabel = CC_STATUS_LABELS[order.status];
  const name =
    (lang === 'ar' ? order.customerNameAr || order.customerName : order.customerName) ??
    t({ en: 'Customer', ar: 'عميل' });

  return (
    <div className={`cc-card cc-card--${order.status}`}>
      <div className="cc-card-top">
        <button
          type="button"
          className="cc-card-no"
          onClick={onOpen}
          title={t({ en: 'Open order', ar: 'فتح الطلب' })}
        >
          #{order.entryNo ?? order.receiptId}
        </button>
        <span className={`cc-status cc-status--${order.status}`}>
          {t({ en: statusLabel.en, ar: statusLabel.ar })}
        </span>
        <span className={`cc-delay cc-delay--${band}`} title={t({ en: 'Waiting', ar: 'الانتظار' })}>
          {ccFormatElapsed(elapsed)}
        </span>
      </div>

      <div className="cc-card-cust">
        <span className="cc-card-name">{name}</span>
        {order.customerPhone && <span className="cc-card-phone">{order.customerPhone}</span>}
      </div>

      {order.addressSummary && <div className="cc-card-addr">{order.addressSummary}</div>}

      {/* When the customer was promised the order. Read off the receipt, so the
          agent, the branch and the Delivery workspace all show the one time.
          Delivery orders only: on a pickup the column merely echoes the order
          time, and printing that as "Deliver" would mislead. */}
      {order.orderType === 2 && order.deliveryAt && (
        <div className="cc-card-deliver">
          <span className="cc-card-deliver-label">{t({ en: 'Deliver', ar: 'التوصيل' })}</span>
          <span className="cc-card-deliver-time">{formatReceiptDateTime(order.deliveryAt)}</span>
        </div>
      )}

      <div className="cc-card-meta">
        {showBranch && order.branchName && (
          <span className="cc-chip cc-chip--branch">{order.branchName}</span>
        )}
        <span className="cc-chip">
          {order.itemCount} {t({ en: 'items', ar: 'صنف' })}
        </span>
        {order.isPrepaid ? (
          <span className="cc-chip cc-chip--paid">{t({ en: 'Prepaid', ar: 'مدفوع مسبقاً' })}</span>
        ) : order.paymentTypeLabel ? (
          <span className="cc-chip cc-chip--paid">{order.paymentTypeLabel}</span>
        ) : (
          <span className="cc-chip cc-chip--unpaid">{t({ en: 'Unpaid', ar: 'غير مدفوع' })}</span>
        )}
        {/* The driver reads in the till's language, not in the language of
            whoever dispatched — see driverNameAr on the wire contract. */}
        {driverLabel && <span className="cc-chip">{driverLabel}</span>}
      </div>

      <div className="cc-card-foot">
        <span className="cc-card-total">{money(order.totalAmount)}</span>
        <span className="cc-card-agent">{order.agentName ?? ''}</span>
      </div>

      {children && <div className="cc-card-actions">{children}</div>}
    </div>
  );
}
